'use client';

import { useAtom } from 'jotai';
import { useTranslations } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import { type ComponentRef, type KeyboardEvent, type MouseEvent, useLayoutEffect, useRef, useState } from 'react';
import IconCancel from '~/shared/assets/icons/cancel.svg';
import IconClose from '~/shared/assets/icons/close.svg';
import IconSearch from '~/shared/assets/icons/search.svg';
import { searchStateAtom } from '../model/state';

const isTypingElement = (t: EventTarget | null): boolean => {
    if (!(t instanceof HTMLElement)) return false;
    const tag = t.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || t.isContentEditable;
};

export default () => {
    const t = useTranslations();
    const searchParams = useSearchParams();
    const [{ open }, setState] = useAtom(searchStateAtom);

    const dialogRef = useRef<ComponentRef<'dialog'>>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const initial = searchParams.get('search') ?? '';
    const [value, setValue] = useState(initial);

    const handleBackdropClick = (event?: MouseEvent<HTMLDialogElement> | KeyboardEvent<HTMLDialogElement>) => {
        if (!dialogRef.current) return;

        if (!event || 'key' in event) {
            setState(prev => ({ ...prev, open: false }));
            return;
        }

        const rect = dialogRef.current.getBoundingClientRect();
        const { clientX, clientY } = event;
        if (clientX < rect.left || clientX > rect.right || clientY < rect.top || clientY > rect.bottom) {
            setState(prev => ({ ...prev, open: false }));
        }
    };

    useLayoutEffect(() => {
        const dialog = dialogRef.current;
        if (!dialog) return;

        const focusEnd = () => {
            const el = inputRef.current;
            if (!el) return;
            el.focus();
            const end = el.value.length;
            try {
                el.setSelectionRange(end, end);
            } catch {}
        };

        if (open) {
            if (!dialog.open) dialog.showModal();
            focusEnd();
            setTimeout(focusEnd, 0);
        } else {
            if (dialog.open) dialog.close();
        }
    }, [open]);

    return (
        <dialog
            ref={dialogRef}
            aria-modal='true'
            aria-labelledby='search-dialog-title'
            className='inset-0 m-0 max-h-screen min-h-screen w-full font-futura outline-none'
            onClick={handleBackdropClick}
            onClose={() => open && setState(prev => ({ ...prev, open: false }))}
            onCancel={e => {
                e.preventDefault();
                setState(prev => ({ ...prev, open: false }));
            }}
            onKeyUp={e => {
                if (isTypingElement(e.target)) return;
                if (e.key === 'Enter' || e.key === ' ') handleBackdropClick(e);
            }}
        >
            <h2 id='search-dialog-title' className='sr-only'>
                {t('search.label')}
            </h2>

            <div className='flex min-h-screen w-full flex-col gap-y-page bg-neutral-50 p-page text-neutral-950'>
                <div className='flex w-full items-center justify-end'>
                    <button
                        type='button'
                        aria-label={t('search.close')}
                        className='p-2'
                        onClick={e => {
                            if (e.currentTarget instanceof HTMLElement && e.nativeEvent.detail !== 0)
                                e.currentTarget.blur();
                            setState(prev => ({ ...prev, open: false }));
                        }}
                    >
                        <IconClose className='size-6 stroke-neutral-500' />
                    </button>
                </div>

                <div className='flex flex-col gap-y-page'>
                    <form action='/products' className='relative'>
                        <label htmlFor='search' className='sr-only'>
                            {t('search.label')}
                        </label>

                        <input
                            ref={inputRef}
                            id='search'
                            type='text'
                            inputMode='search'
                            enterKeyHint='search'
                            name='search'
                            placeholder={t('search.placeholder')}
                            className='w-full border-2 border-neutral-300 bg-transparent p-2 pr-20 text-18 outline-none outline-2 focus:border-blue-600'
                            defaultValue={searchParams.get('search') ?? ''}
                            onInput={e => setValue(e.currentTarget.value)}
                        />

                        {!!value && (
                            <button
                                type='button'
                                aria-label={t('search.clear')}
                                className='absolute inset-y-0 right-10 my-auto grid size-8 place-items-center rounded-md'
                                onClick={() => {
                                    const el = inputRef.current;
                                    if (!el) return;

                                    el.value = '';
                                    setValue('');
                                    el.focus();

                                    try {
                                        el.setSelectionRange(0, 0);
                                    } catch {}
                                }}
                            >
                                <IconCancel className='size-5 stroke-neutral-500' />
                            </button>
                        )}

                        <div className='pointer-events-none absolute inset-y-0 right-2 my-auto h-fit rounded-2 outline-2 outline-neutral-400 focus-visible:outline'>
                            <IconSearch className='size-6 stroke-neutral-400' />
                        </div>
                    </form>
                </div>
            </div>
        </dialog>
    );
};
